import { useState } from 'react';
import { contactsAPI } from '../api/client';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ko', label: 'Korean', flag: '🇰🇷' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸' },
  { code: 'zh', label: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { code: 'fr', label: 'French', flag: '🇫🇷' },
  { code: 'de', label: 'German', flag: '🇩🇪' },
  { code: 'pt', label: 'Portuguese', flag: '🇧🇷' },
  { code: 'ar', label: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { code: 'th', label: 'Thai', flag: '🇹🇭' },
  { code: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  { code: 'id', label: 'Indonesian', flag: '🇮🇩' },
];

interface LanguageBadgeProps {
  contactId: number;
  language: string | null;
  detectedLanguage: string | null;
  onLanguageChanged?: (language: string | null) => void;
}

export function LanguageBadge({ contactId, language, detectedLanguage, onLanguageChanged }: LanguageBadgeProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = LANGUAGES.find(l => l.code === language);
  const label = current ? `${current.flag} ${current.label}` : language || '🌐 Set language';

  const handleSelect = async (code: string | null) => {
    setSaving(true);
    try {
      await contactsAPI.updateLanguage(contactId, code);
      onLanguageChanged?.(code || detectedLanguage);
    } catch {
      // ignore
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent/70 hover:bg-accent/20 transition-colors"
        disabled={saving}
      >
        {label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-edge rounded-lg shadow-lg py-1 w-44 max-h-60 overflow-y-auto">
            {detectedLanguage && (
              <button
                onClick={() => handleSelect(null)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-ink/5 text-ink/50"
              >
                Auto-detected ({detectedLanguage})
              </button>
            )}
            {LANGUAGES.map(l => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-ink/5 ${
                  l.code === language ? 'text-accent font-medium' : 'text-ink/70'
                }`}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
